import React, { FC } from 'react';
import { createPictureFactory, Seo, useSeoDefaults, I18n } from '@funtech-int/yango-shared';
import { jsonLdScriptProps } from 'react-schemaorg';

import { filmRoute } from '@/scenarios/movie/routes';
import { useI18n } from '@/shared/i18n/seo/useI18n';
import { MovieCardResourceQueryResult } from '@/scenarios/movie/hooks/useMovieCard';
import { MovieWebPageResourceQueryResult } from '@/scenarios/movie/lib/movieWebPageResource';
import { checkIsTvSeries } from '@/shared/lib/movie';

type Link = {
    href: string;
    as: string;
    type?: string;
};

interface YearsRange {
    start?: number | null;
    end?: number | null;
}

function presentYearsRange({ start, end }: YearsRange = {}) {
    if (end && start !== end) {
        return start ? `${start}-${end}` : '';
    }
    return start ? `${start}` : '';
}

interface FormatParams {
    i18n: I18n;
    ottProductionYear: number | null;
    country: string | null;
}
function formatFilmYearAndCountry({
    i18n,
    ottProductionYear,
    country,
}: FormatParams): string | null {
    if (!ottProductionYear && !country) {
        return null;
    }

    const showComma = ottProductionYear && country ? ', ' : null;

    return i18n('movie__film_seo_year_and_country', {
        ottProductionYear,
        comma: showComma,
        country,
    });
}

interface MoviePageHeadViewProps {
    title: string;
    description: string;
    ogImage: string;
    ogImageAlt: string;
    ogVideoDuration: number | undefined;
    ogVideoTags: string[];
    scripts: Array<{ id: string; data: object }>;
    location: ReturnType<typeof filmRoute>;
    preloadLinks?: Link[];
}

const MoviePageHeadView: FC<MoviePageHeadViewProps> = ({
    title,
    description,
    ogImage,
    location,
    ogImageAlt,
    ogVideoDuration,
    ogVideoTags,
    scripts,
    preloadLinks,
}) => (
    <>
        <Seo.BasicMeta seoTitle={title} seoDescription={description} location={location} />
        <Seo.OgVideoMeta
            ogTitle={title}
            ogDescription={description}
            ogImage={ogImage}
            location={location}
            ogImageAlt={ogImageAlt}
            ogVideoDuration={ogVideoDuration}
            ogVideoTags={ogVideoTags}
        />
        <Seo.TwitterMeta twitterDescription={description} twitterImage={ogImage} />
        <Seo.StructuredData scripts={scripts} />
        {preloadLinks && <Seo.PreloadLinks links={preloadLinks} />}
    </>
);

const pictureFactory = createPictureFactory({
    fallback: {
        src: '2016x1134',
    },
});

const ogImageFactory = createPictureFactory({
    fallback: {
        src: '1200x630',
    },
});

const vieoPreviewFactory = createPictureFactory({
    fallback: {
        src: '816x459',
    },
});

interface MoviePageHeadProps {
    movie: MovieCardResourceQueryResult;
    movieWebPage: MovieWebPageResourceQueryResult;
    contentId: string | undefined;
}

export const MoviePageHead: FC<MoviePageHeadProps> = ({ movie, movieWebPage, contentId }) => {
    const { seoSiteName, seoImage } = useSeoDefaults();
    const i18n = useI18n();

    const movieForSeo = movie ?? null;

    let title = seoSiteName;
    let description = seoSiteName;
    let ogVideoDuration;
    let uploadDate;
    const movieTitle = movie?.title.toString() || '';

    if (movieTitle && (movie?.typename === 'Video' || movie?.typename === 'Film')) {
        title = i18n('movie__film_seo_title', { title: movieTitle, brandName: seoSiteName });
        const country = movie.countries?.[0] || null;
        const { productionYear: ottProductionYear } = movie;

        const yearAndCountry = formatFilmYearAndCountry({
            i18n,
            ottProductionYear: Number(ottProductionYear),
            country,
        });

        description = i18n('movie__film_meta_description', {
            title: movieTitle,
            yearAndCountry,
            brandName: seoSiteName,
        });

        ogVideoDuration = movie.filmDuration ? movie.filmDuration * 60 : undefined;
        uploadDate = movie?.productionYear ? movie?.productionYear : undefined;
    }

    if (movieTitle && checkIsTvSeries(movie?.typename)) {
        title = i18n('movie__series_seo_title', { title: movieTitle, brandName: seoSiteName });
        description = i18n('movie__series_meta_description', {
            title: movieTitle,
            years: presentYearsRange(movie?.releaseYears ?? undefined),
            brandName: seoSiteName,
        });
        if (movie && 'releaseYears' in movie) {
            uploadDate = movie?.releaseYears?.start
                ? movie?.releaseYears?.start.toString()
                : undefined;
        }
    }

    const ogImage =
        ogImageFactory({
            fallbackUrl: movieWebPage?.cover?.toString(),
        }).img.src ?? seoImage.url;

    const rawGenres = movieForSeo?.genres ?? [];
    const ogVideoTags = rawGenres.filter((s): s is string => Boolean(s));

    const scripts = movieForSeo
        ? [
              {
                  id: 'video-object',
                  data: jsonLdScriptProps({
                      '@context': 'https://schema.org',
                      '@type': 'VideoObject',
                      name: movie?.title.toString(),
                      description: movie?.shortDescription || undefined,
                      thumbnailUrl: movie?.verticalPoster
                          ? vieoPreviewFactory({ fallbackUrl: movie?.verticalPoster.toString() })
                                .img.src
                          : undefined,
                      uploadDate,
                      duration: ogVideoDuration !== undefined ? String(ogVideoDuration) : undefined,
                      contentUrl: undefined,
                  }),
              },
          ]
        : [];

    const ogImageAlt = description;

    const location = filmRoute({ id: contentId ?? '' });

    const { img } = pictureFactory({
        fallbackUrl: movie?.cover?.toString(),
    });

    const preloadLinks = [
        {
            href: img.src ?? '',
            as: 'image',
        },
    ];

    return (
        <MoviePageHeadView
            title={title}
            description={description}
            ogImage={ogImage}
            location={location}
            ogImageAlt={ogImageAlt}
            ogVideoDuration={ogVideoDuration}
            ogVideoTags={ogVideoTags}
            scripts={scripts}
            preloadLinks={preloadLinks}
        />
    );
};
